import type { WorkerRequest, WorkerResponse, WorkerSuccess } from "./imageWorker";
import type { CropRect, ProcessingOptions } from "./types";

const CONCURRENCY = 3;

export interface PoolJob {
  id: string;
  fileName: string;
  data: ArrayBuffer;
  crop?: CropRect;
  outputName?: string;
  cropAspect?: number;
  // Byte-Budget für die JPEG-Daten dieses Bildes (MAX_FILE_BYTES minus EXIF-Anteil).
  maxBytes?: number;
}

export type JobResult =
  | { ok: true; result: WorkerSuccess }
  | { ok: false; id: string; fileName: string; message: string };

export class WorkerPool {
  private workers: Worker[] = [];
  private idle: Worker[] = [];
  private queue: Array<{ job: PoolJob; resolve: (r: JobResult) => void }> = [];
  private options: ProcessingOptions;

  constructor(options: ProcessingOptions, size = CONCURRENCY) {
    this.options = options;
    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL("./imageWorker.ts", import.meta.url), { type: "module" });
      this.workers.push(worker);
      this.idle.push(worker);
    }
  }

  process(job: PoolJob): Promise<JobResult> {
    return new Promise((resolve) => {
      this.queue.push({ job, resolve });
      this.drain();
    });
  }

  private drain() {
    while (this.idle.length > 0 && this.queue.length > 0) {
      const worker = this.idle.shift()!;
      const next = this.queue.shift()!;
      this.runOnWorker(worker, next.job, next.resolve);
    }
  }

  private runOnWorker(worker: Worker, job: PoolJob, resolve: (r: JobResult) => void) {
    const handleMessage = (event: MessageEvent<WorkerResponse>) => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      this.idle.push(worker);

      const response = event.data;
      if (response.ok) {
        resolve({ ok: true, result: response });
      } else {
        resolve({ ok: false, id: response.id, fileName: response.fileName, message: response.message });
      }
      this.drain();
    };

    const handleError = (event: ErrorEvent) => {
      worker.removeEventListener("message", handleMessage);
      worker.removeEventListener("error", handleError);
      this.idle.push(worker);
      resolve({ ok: false, id: job.id, fileName: job.fileName, message: event.message });
      this.drain();
    };

    worker.addEventListener("message", handleMessage);
    worker.addEventListener("error", handleError);

    const request: WorkerRequest = {
      id: job.id,
      fileName: job.fileName,
      data: job.data,
      options: { ...this.options, maxBytes: job.maxBytes ?? this.options.maxBytes },
      crop: job.crop,
      outputName: job.outputName,
      cropAspect: job.cropAspect,
    };
    worker.postMessage(request, [job.data]);
  }

  terminate() {
    for (const w of this.workers) w.terminate();
    this.workers = [];
    this.idle = [];
    this.queue = [];
  }
}
